import Vue from 'vue';
import {
  formatDate,
  formatTime,
  formatGeneral,
  formatNumeral,
  unformatGeneral,
  unformatNumeral,
  registerCursorTracker,
} from 'cleave-zen';

// Buefy's b-datepicker/b-timepicker fall back to native <input type="date"/"time">
// on mobile (mobile-native prop). The mask only makes sense for free-text typing,
// so it's skipped for native inputs.
const NATIVE_INPUT_TYPES = ['date', 'time'];

// CNPJ alfanumérico: as 12 primeiras posições aceitam letras (A-Z) e dígitos,
// as 2 últimas (dígitos verificadores) continuam exclusivamente numéricas.
// `cleave-zen`'s `formatGeneral` só oferece `numericOnly` (tudo ou nada), sem
// um modo "alfanumérico" nativo, então filtramos o valor cru nós mesmos antes
// de delegar a ele o posicionamento dos blocos/delimitadores (`.` `.` `/` `-`).
// `@fnando/cnpj` (`isValid`/`format`) continua sendo a validação/normalização
// autoritativa na camada de negócio; esta função só evita que o usuário digite
// caracteres inválidos.
function sanitizeCnpjInput(value) {
  const raw = String(value || '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
  const head = raw.slice(0, 12);
  const tail = raw.slice(12, 14).replace(/\D/g, '');
  return head + tail;
}

const mascaras = {
  telefone(modifiers, value) {
    const digits = (value || '').replace(/\D/g, '');
    const isCelular = digits.length > 10;
    return {
      type: 'general',
      options: {
        numericOnly: true,
        blocks: isCelular ? [2, 5, 4] : [2, 4, 4],
        delimiters: [' ', '-'],
      },
    };
  },
  valor(modifiers = {}) {
    const scale = modifiers.scale ? 4 : 2;
    return {
      type: 'numeral',
      options: {
        prefix: !modifiers.integer && !modifiers.noPrefix ? 'R$ ' : '',
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralDecimalScale: !modifiers.integer ? scale : 0,
        numeralPositiveOnly: true,
      },
    };
  },
  porcentagem(modifiers = {}) {
    const scale = modifiers.scale ? 4 : 2;
    return {
      type: 'numeral',
      options: {
        numeralDecimalMark: ',',
        delimiter: '.',
        numeralIntegerScale: 3,
        numeralDecimalScale: scale || 0,
        numeralPositiveOnly: true,
        stripLeadingZeroes: false,
      },
    };
  },
  cnpj() {
    return {
      type: 'general',
      // `numericOnly` desligado + pré-sanitização própria (`sanitizeCnpjInput`,
      // aplicada em `formatValue`/`unformatValue` abaixo) em vez do padrão
      // `numericOnly: true` usado pelas outras máscaras: ver comentário de
      // `sanitizeCnpjInput` para o porquê.
      sanitize: sanitizeCnpjInput,
      options: {
        numericOnly: false,
        uppercase: true,
        delimiters: ['.', '.', '/', '-'],
        blocks: [2, 3, 3, 4, 2],
      },
    };
  },
  cpf() {
    return {
      type: 'general',
      options: {
        numericOnly: true,
        delimiters: ['.', '.', '-'],
        blocks: [3, 3, 3, 2],
      },
    };
  },
  inscricaoEstadual() {
    return {
      type: 'general',
      options: {
        numericOnly: true,
        delimiters: ['.', '.', '.'],
        blocks: [3, 3, 3, 3],
      },
    };
  },
  cep() {
    return {
      type: 'general',
      options: {
        numericOnly: true,
        delimiters: ['-'],
        blocks: [5, 3],
      },
    };
  },
  data() {
    return {
      type: 'date',
      options: {
        datePattern: ['d', 'm', 'Y'],
      },
    };
  },
  hora() {
    return {
      type: 'time',
      options: {
        timePattern: ['h', 'm'],
      },
    };
  },
  documento(modifiers, value) {
    // CNPJ alfanumérico: contar apenas dígitos (`\D`) para decidir entre
    // CPF/CNPJ subestima o tamanho real assim que o usuário digita letras
    // (ex.: "12ABC34501DE35" tem só 9 dígitos, mas 14 caracteres), fazendo o
    // dispatcher aplicar a máscara de CPF por engano. Removendo qualquer
    // caractere não alfanumérico (não só pontuação — também espaços e outros
    // separadores de um paste), o tamanho da string reflete letras + dígitos
    // corretamente.
    //
    // Isso sozinho ainda não basta: para um CNPJ como "12ABC...", enquanto o
    // usuário digitou só "12" (2 caracteres, ≤ 11) o dispatcher escolheria
    // CPF — cuja máscara é `numericOnly`, e apagaria a próxima letra digitada
    // antes mesmo de o comprimento passar de 11. CPF nunca tem letra, então
    // qualquer letra presente já denuncia CNPJ, independente do tamanho.
    const clean = (value || '').replace(/[^A-Za-z0-9]/g, '');
    const temLetra = /[A-Za-z]/.test(clean);
    return temLetra || clean.length > 11 ? mascaras.cnpj() : mascaras.cpf();
  },
};

function formatValue(arg, modifiers, value) {
  const mask = mascaras[arg](modifiers, value);

  if (mask.type === 'date') return formatDate(value, mask.options);
  if (mask.type === 'time') return formatTime(value, mask.options);
  if (mask.type === 'numeral') return formatNumeral(value, mask.options);
  const sanitized = mask.sanitize ? mask.sanitize(value) : value;
  return formatGeneral(sanitized, mask.options);
}

function unformatValue(arg, modifiers, value) {
  const mask = mascaras[arg](modifiers, value);

  if (mask.type === 'numeral') {
    return unformatNumeral(value, { numeralDecimalMark: mask.options.numeralDecimalMark });
  }
  if (mask.type === 'general') {
    const sanitized = mask.sanitize ? mask.sanitize(value) : value;
    return unformatGeneral(sanitized, {
      delimiters: mask.options.delimiters,
      delimiter: mask.options.delimiter,
    });
  }
  return value;
}

function cursorTrackerProps(arg, modifiers, value) {
  const { options } = mascaras[arg](modifiers, value);
  return options.delimiters
    ? { delimiters: options.delimiters, prefix: options.prefix }
    : { delimiter: options.delimiter, prefix: options.prefix };
}

Vue.directive('mascara', {
  bind(el, binding) {
    const input = el.querySelector('input');
    if (NATIVE_INPUT_TYPES.includes(input.type)) return;

    const handleInput = () => {
      const formatted = formatValue(binding.arg, binding.modifiers, input.value);
      if (formatted === input.value) return;

      // A plain `input.value = formatted` assignment gets silently reverted by
      // Vue/Buefy's own v-model sync on the same input event in some browsers
      // (observed in Firefox). execCommand replaces the value through the native
      // editing pipeline instead, which the framework's own listeners pick up
      // consistently across browsers.
      input.focus();
      input.setSelectionRange(0, input.value.length);
      const applied = document.execCommand('insertText', false, formatted);
      if (!applied) {
        input.value = formatted;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    };
    input.addEventListener('input', handleInput);
    input.mascaraHandleInput = handleInput;

    // A few components read the old Cleave.js instance API directly
    // (`event.target.vCleave.getRawValue()/.getFormattedValue()`) instead of going
    // through this directive's own formatting. Keep that surface working: the
    // input is always kept formatted by handleInput above, so getFormattedValue
    // is just its current value, and getRawValue reverses the same mask config.
    input.vCleave = {
      getRawValue: () => unformatValue(binding.arg, binding.modifiers, input.value),
      getFormattedValue: () => input.value,
    };

    if (input.value) {
      const formatted = formatValue(binding.arg, binding.modifiers, input.value);
      if (formatted !== input.value) {
        input.value = formatted;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    // `documento` dynamically switches shape (cpf <-> cnpj) as digits are typed;
    // registerCursorTracker expects a fixed delimiter list, so it's skipped there.
    // Formatting itself still works correctly, only cursor positioning is naive.
    if (binding.arg !== 'documento') {
      input.mascaraCursorDestructor = registerCursorTracker({
        input,
        ...cursorTrackerProps(binding.arg, binding.modifiers, input.value),
      });
    }
  },
  unbind(el) {
    const input = el.querySelector('input');
    if (input.mascaraHandleInput) input.removeEventListener('input', input.mascaraHandleInput);
    if (input.mascaraCursorDestructor) input.mascaraCursorDestructor();
  },
});
